import { Autocomplete, IconButton, ListItem, TextField } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import { SyntheticEvent, useState } from "react"
import { addEdgeToNode } from "@features/builder/helpers/add-edge"
import { AppNode, EdgeData, NodeType } from "@features/builder/graph/types"
import { Edge, useEdges, useNodes, useReactFlow } from "@xyflow/react"
import { getChildrenNodes, getParentNodes } from "@features/builder/helpers/nodes"
import { useGetLinkedNode } from "@features/builder/hooks/useGetLinkedNode"
import { isEdgeConnectionValid } from "@features/builder/NodeDrawer/NodeForm/GraphNav/helpers"
import { sortNodesAlphabetically } from "@utils/node/sortNodesAlphabetically"

type AddMode = "children" | "parents"

export const GraphNavAddEdge = (props: { node: AppNode; mode: AddMode; disabled: boolean }) => {
  const [toAdd, setToAdd] = useState<{ id: string; type: string; label: string } | null>(null)
  const [autocompleteValue, setAutocompleteValue] = useState<string>("")
  const { getLinkedNode } = useGetLinkedNode()
  const reactFlowInstance = useReactFlow<AppNode, Edge<EdgeData>>()
  const nodes = useNodes<AppNode>()
  const edges = useEdges<Edge>()

  const toFilter = [props.node.id]
  toFilter.push(...getParentNodes(props.node, nodes).map(({ id }) => id))
  toFilter.push(...getChildrenNodes(props.node, nodes).map((node) => node.id))

  const filterOptions = () =>
    nodes
      .filter((n) => !toFilter.includes(n.id) && n.type !== NodeType.Group)
      .sort(sortNodesAlphabetically)
      .map((node) => ({ id: node.id, type: node.type, label: node.data.label ?? "" }))

  const allOptions = filterOptions()

  const options = autocompleteValue
    ? allOptions.filter(
        (o) => (o.label?.toLowerCase() ?? "").indexOf(autocompleteValue.toLowerCase()) > -1,
      )
    : allOptions

  const handleAddEdge = async () => {
    if (!toAdd) return
    const node = nodes.find((n) => n.id === toAdd.id)
    if (!node) return

    let updatedNodes: AppNode[] = []
    if (props.mode === "children" && isEdgeConnectionValid(props.node, node, nodes, edges)) {
      const linkedNodeId =
        "metaNode" in props.node.data ? props.node.data.metaNode?.Link : undefined
      const linkedNode = await getLinkedNode(linkedNodeId)
      updatedNodes = addEdgeToNode(props.node, node, nodes, linkedNode)
    } else if (props.mode === "parents" && isEdgeConnectionValid(node, props.node, nodes, edges)) {
      const linkedNodeId = "metaNode" in node.data ? node.data.metaNode?.Link : undefined
      const linkedNode = await getLinkedNode(linkedNodeId)
      updatedNodes = addEdgeToNode(node, props.node, nodes, linkedNode)
    }
    updatedNodes.forEach((updatedNode) => {
      reactFlowInstance.updateNode(updatedNode.id, updatedNode, { replace: true })
    })
    setToAdd(null)
    setAutocompleteValue("")
  }

  const handleChange = (
    _e: SyntheticEvent,
    newValue: string | { id: string; type: string; label: string } | null,
  ) => {
    if (typeof newValue === "string") return
    setToAdd(newValue)
  }
  return (
    <ListItem divider>
      <Autocomplete
        freeSolo
        disabled={props.disabled}
        size="small"
        fullWidth
        value={toAdd}
        inputValue={autocompleteValue}
        onChange={handleChange}
        onInputChange={(_e, newValue) => setAutocompleteValue(newValue)}
        renderInput={(params) => {
          return <TextField {...params} variant={"outlined"} required fullWidth />
        }}
        options={options}
      />
      <IconButton onClick={handleAddEdge} disabled={props.disabled || !toAdd}>
        <AddIcon />
      </IconButton>
    </ListItem>
  )
}
